import React, { useState, useEffect } from "react";
import { Grid, TextField, Button, Card, CardContent, Typography, Box, useMediaQuery, useTheme, Snackbar, Alert, CircularProgress } from "@mui/material";
import Underframe from "./assets/Underframe.png";
import image from "./assets/image.svg";

const CustomUsecaseForm = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  // Form state
  const [formData, setFormData] = useState({
    email: "",
    contact: "+91",
    query: ""
  });
  
  // Form validation state
  const [validation, setValidation] = useState({
    email: { valid: true, message: "" },
    contact: { valid: true, message: "" }
  });
  
  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  
  // Google Script URL - replace with your deployed web app URL
  const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzVlrahnbfz8tvKFhH0w-yx2rUwoqxZMRbX7c8qmNGrMc7TvSueQDGVLY4WCObp7XY9/exec";

  // Validate email format
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(com|org|net|edu|gov|mil|co|in|io|ai|tech|info|biz|[a-zA-Z]{2,})$/;
    return emailRegex.test(email);
  };

  // Validate phone number format
  const validateContact = (contact) => {
    // Allow +, spaces, and numbers. Minimum 8 digits, maximum 15 digits.
    const phoneRegex = /^\+?[0-9\s]{8,15}$/;
    return phoneRegex.test(contact);
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    
    // Clear validation errors when typing
    if (validation[name] && !validation[name].valid) {
      setValidation(prev => ({
        ...prev,
        [name]: { valid: true, message: "" }
      }));
    }
  };

  // Validate form fields
  const validateForm = () => {
    let isValid = true;
    const newValidation = { ...validation };
    
    // Validate email
    if (!formData.email.trim()) {
      newValidation.email = { valid: false, message: "Email is required" };
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newValidation.email = { valid: false, message: "Please enter a valid email address" };
      isValid = false;
    } else {
      newValidation.email = { valid: true, message: "" };
    }
    
    // Validate contact (only if provided - not required)
    if (formData.contact && formData.contact !== "+91") {
      if (!validateContact(formData.contact)) {
        newValidation.contact = { valid: false, message: "Please enter a valid phone number" };
        isValid = false;
      } else {
        newValidation.contact = { valid: true, message: "" };
      }
    }
    
    setValidation(newValidation);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    // Use a direct form submission method
    const form = e.target;
    const formDataObj = new FormData(form);
    
    fetch(SCRIPT_URL, {
      method: 'POST',
      mode: 'no-cors', // This is important for cross-origin requests to Google
      body: formDataObj
    })
    .then(() => {
      // Reset form
      setFormData({
        email: "",
        contact: "+91",
        query: ""
      });
      
      setSnackbar({
        open: true,
        message: "Thank you for your query! We'll get back to you soon.",
        severity: "success"
      });
    })
    .catch(error => {
      console.error('Error!', error.message);
      
      setSnackbar({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error"
      });
    })
    .finally(() => {
      setIsSubmitting(false);
    });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  const currentPath = window.location.pathname;
  const isDarkHome = currentPath.endsWith('/darkhome');
  const isContactUs = currentPath.endsWith('/contact-us');
  
  // Determine the heading text based on the current path
  const getHeadingText = () => {
    if (isContactUs) {
      return "Contact Us";
    }
    return "Have a Custom Usecase in mind?";
  };
  
  return (
    <Box
      sx={{
        color: "#828282",
        padding: { xs: "40px 0", sm: "50px 0", md: "60px 0 40px" },
        borderTop: "1px solid #333",
        borderRadius: { xs: "12px", sm: "16px", md: "20px" },
        backgroundImage: isDarkHome ? 'none' : `url(${Underframe})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        justifyContent: "center",
        width: "100%",
        overflow: "hidden" // Prevent content from exceeding boundaries
      }}
    >
      <Box sx={{ 
        width: { xs: "95%", sm: "90%", md: "85%", lg: "70%" },
        maxWidth: "1200px",
        mx: "auto", // Ensure horizontal centering
        px: { xs: 1, sm: 2, md: 2 } // Add horizontal padding inside
      }}>
        <Typography
          variant="h4"
          align="center"
          gutterBottom
          sx={{ 
            color: "#fff", 
            fontWeight: 600, 
            mb: { xs: 2, sm: 3 },
            fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" }
          }}
        >
          {getHeadingText()}
        </Typography>

        <Grid container spacing={{ xs: 2, sm: 3 }} justifyContent="center" alignItems="stretch" sx={{ width: "100%", mx: 0 }}>
          <Grid item xs={12} md={7} sx={{ mb: { xs: 3, md: 4 } }}>
            <Card sx={{
              background: "rgba(16, 16, 16, 0.6)",
              backdropFilter: "blur(10px)",
              color: "#fff",
              borderRadius: { xs: "12px", sm: "16px", md: "20px" },
              p: { xs: 1.5, sm: 2 },
              display: "flex",
              flexDirection: "column",
              height: "100%",
              border: "1px solid rgba(51, 51, 51, 0.7)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)"
            }}>
              <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 } }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: { xs: "20px", sm: "22px", md: "24px" }, 
                    mb: { xs: 1.5, sm: 2 } 
                  }}
                >
                  Send Query
                </Typography>
                <form onSubmit={handleSubmit}>
                  <Box mb={{ xs: 1.5, sm: 2 }}>
                    <Typography variant="body1" sx={{ color: "#fff", mb: 0.75, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                      Email*
                    </Typography>
                    <TextField
                      fullWidth
                      name="email"
                      variant="outlined"
                      placeholder="you@company.com"
                      size={isMobile ? "small" : "medium"}
                      value={formData.email}
                      onChange={handleChange}
                      required
                      error={!validation.email.valid}
                      helperText={validation.email.message}
                      FormHelperTextProps={{
                        sx: { color: "#ff4d4f !important" }
                      }}
                      InputProps={{
                        sx: {
                          color: "#9e9e9e",
                          borderRadius: "8px",
                          backgroundColor: "rgba(16, 16, 16, 0.6)",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43 !important",
                          },
                          "&.Mui-focused": {
                            color: "#9e9e9e",
                          },
                          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ff4d4f !important",
                          },
                        },
                        notchedOutline: {
                          borderColor: "#e95f43",
                        }
                      }}
                    />
                  </Box>
                  <Box mb={{ xs: 1.5, sm: 2 }}>
                    <Typography variant="body1" sx={{ color: "#fff", mb: 0.75, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                      Contact
                    </Typography>
                    <TextField
                      fullWidth
                      name="contact"
                      variant="outlined"
                      value={formData.contact}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                      error={!validation.contact.valid}
                      helperText={validation.contact.message}
                      FormHelperTextProps={{
                        sx: { color: "#ff4d4f !important" }
                      }}
                      InputProps={{
                        sx: {
                          color: "#9e9e9e",
                          borderRadius: "8px",
                          backgroundColor: "rgba(16, 16, 16, 0.6)",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43 !important",
                          },
                          "&.Mui-focused": {
                            color: "#9e9e9e",
                          },
                          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#ff4d4f !important",
                          },
                        },
                        notchedOutline: {
                          borderColor: "#e95f43",
                        }
                      }}
                    />
                  </Box>
                  <Box mb={{ xs: 1.5, sm: 2 }}>
                    <Typography variant="body1" sx={{ color: "#fff", mb: 0.75, fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                      How can we help you?
                    </Typography>

                    <TextField
                      fullWidth
                      name="query"
                      variant="outlined"
                      placeholder="Type here..."
                      multiline
                      minRows={isMobile ? 3 : 4}
                      value={formData.query}
                      onChange={handleChange}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        sx: {
                          color: "#9e9e9e",
                          borderRadius: "8px",
                          backgroundColor: "rgba(16, 16, 16, 0.6)",
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43",
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#e95f43 !important",
                          },
                          "&.Mui-focused": {
                            color: "#9e9e9e",
                          },
                        },
                        notchedOutline: {
                          borderColor: "#e95f43",
                        }
                      }}
                    />
                  </Box>
                  <Box display="flex" justifyContent="flex-start">
                    <Button 
                      type="submit"
                      variant="contained" 
                      disabled={isSubmitting}
                      sx={{ 
                        mt: { xs: 1, sm: 2 }, 
                        background: "#000", 
                        color: "#fff", 
                        borderRadius: "20px", 
                        px: { xs: 2, sm: 3 }, 
                        py: { xs: 0.75, sm: 1 },
                        fontSize: { xs: "0.875rem", sm: "1rem" },
                        textTransform: "none",
                        "&:hover": {
                          background: "#333"
                        }
                      }}
                    >
                      {isSubmitting ? (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                          Sending...
                        </Box>
                      ) : "Send Query"}
                    </Button>
                  </Box>
                </form>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5} sx={{ display: "flex", mb: { xs: 3, md: 4 } }}>
            <Card sx={{
              background: "rgba(16, 16, 16, 0.6)",
              backdropFilter: "blur(10px)",
              color: "#fff",
              borderRadius: { xs: "12px", sm: "16px", md: "20px" },
              p: { xs: 1.5, sm: 2 },
              display: "flex",
              flexDirection: "column",
              height: "100%",
              border: "1px solid rgba(51, 51, 51, 0.7)",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.2)"
            }}>
              <CardContent sx={{ flexGrow: 1, p: { xs: 1.5, sm: 2 }, display: "flex", flexDirection: "column" }}>
                <Typography 
                  variant="h6" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 600, 
                    fontSize: { xs: "20px", sm: "22px", md: "24px" }, 
                    mb: { xs: 1.5, sm: 2 } 
                  }}
                >
                  Talk to an AI expert
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#9E9E9E", 
                    fontWeight: 500, 
                    fontSize: { xs: "0.9rem", sm: "1rem", md: "1.125rem" },
                    mb: 2
                  }}
                >
                  Get a free 15-minute consultation with our Automation experts. We can discuss Pricing, Integrations, or try the app live on your own documents.
                </Typography>
                <Box 
                  sx={{ 
                    flexGrow: 1, 
                    display: "flex", 
                    justifyContent: "center", 
                    alignItems: "center",
                    position: "relative",
                    my: { xs: 1, sm: 2 },
                    minHeight: { xs: "120px", sm: "150px", md: "180px" }
                  }}
                >
                  <img
                    src={image}
                    alt="AI Expert"
                    style={{
                      width: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                      borderRadius: "5px"
                    }}
                  />
                </Box>
                <Box display="flex" justifyContent="flex-start" sx={{ mt: "auto" }}>
                  <Button 
                    variant="contained" 
                    sx={{ 
                      mt: { xs: 1, sm: 2 }, 
                      background: "#000", 
                      color: "#fff", 
                      borderRadius: "20px", 
                      px: { xs: 2, sm: 3 }, 
                      py: { xs: 0.75, sm: 1 },
                      fontSize: { xs: "0.875rem", sm: "1rem" },
                      textTransform: "none",
                      "&:hover": {
                        background: "#333"
                      }
                    }}
                  >
                    Request a Demo
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
      
      {/* Feedback Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity} 
          sx={{ width: '100%', borderRadius: '8px' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomUsecaseForm;